import { createBrowserRouter, Navigate } from 'react-router-dom';

import { PublicLayout } from '../components/PublicLayout';
import { PortalLayout } from '../components/PortalLayout';
import { CheckinLayout } from '../components/CheckinLayout';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { Placeholder } from '../components/Placeholder';

import { HomePage } from '../pages/public/HomePage';
import { EventsPage } from '../pages/public/EventsPage';
import { EventPageHost } from '../pages/public/EventPageHost';
import { ArtistsPage } from '../pages/public/ArtistsPage';
import { ArtistProfilePage } from '../pages/public/ArtistProfilePage';
import { InvestPage } from '../pages/public/InvestPage';
import { OfferingDetailPage, RegisterInterestPage } from '../pages/public/OfferingDetailPage';
import { CheckoutPage } from '../pages/public/CheckoutPage';
import { OrderConfirmationPage } from '../pages/public/OrderConfirmationPage';
import { TicketsPage } from '../pages/public/TicketsPage';
import { OrdersPage } from '../pages/buyer/OrdersPage';
import { AccountPage } from '../pages/buyer/AccountPage';
import { AdminHome } from '../pages/admin/AdminHome';
import { AdminAffiliates } from '../pages/admin/AdminAffiliates';
import { AdminSpvs } from '../pages/admin/AdminSpvs';
import { AdminPlugins } from '../pages/admin/AdminPlugins';
import { AdminInvestorApprovals } from '../pages/admin/AdminInvestorApprovals';
import { AdminDistributions } from '../pages/admin/AdminDistributions';
import { AdminCompliance } from '../pages/admin/AdminCompliance';
import { AdminUsers } from '../pages/admin/AdminUsers';
import { AdminArtists } from '../pages/admin/AdminArtists';
import { AdminFinancials } from '../pages/admin/AdminFinancials';
import { AdminAudit } from '../pages/admin/AdminAudit';
import { AdminOrders } from '../pages/admin/AdminOrders';
import { AdminOpportunities } from '../pages/admin/AdminOpportunities';
import { AdminPersonas } from '../pages/admin/AdminPersonas';
import { AdminNetwork } from '../pages/admin/AdminNetwork';
import { AdminInvestments } from '../pages/admin/AdminInvestments';
import { AdminOfferingPreview } from '../pages/admin/AdminOfferingPreview';
import { AdminVenues } from '../pages/admin/AdminVenues';
import { ArtistDashboard } from '../pages/artist/ArtistDashboard';
import { ArtistBookings } from '../pages/artist/ArtistBookings';
import { BookingReview } from '../pages/artist/BookingReview';
import { ArtistProfile } from '../pages/artist/ArtistProfile';
import { ArtistRatings } from '../pages/artist/ArtistRatings';
import { VenueBrowse } from '../pages/organizer/VenueBrowse';
import { VenueDashboard } from '../pages/venue/VenueDashboard';
import { VenueProfilePage } from '../pages/venue/VenueProfile';
import { VenueCalendar } from '../pages/venue/VenueCalendar';
import { VenueBookings } from '../pages/venue/VenueBookings';
import { VenueEventDay } from '../pages/venue/VenueEventDay';
import { OrganizerDashboard } from '../pages/organizer/OrganizerDashboard';
import { EventWizard } from '../pages/organizer/EventWizard';
import { AffiliateDashboardPage } from '../pages/public/AffiliateDashboard';
import { OrganizerPayouts } from '../pages/organizer/OrganizerPayouts';
import { EventManage } from '../pages/organizer/EventManage';
import { InvestorDashboard } from '../pages/investor/InvestorDashboard';
import { InvestorInvestments } from '../pages/investor/InvestorInvestments';
import { InvestmentLedger } from '../pages/investor/InvestmentLedger';
import { LoginPage } from '../pages/public/LoginPage';
import { RegisterPage } from '../pages/public/RegisterPage';
import { ForbiddenPage, NotFoundPage, RouteErrorPage } from '../pages/public/StatusPages';
import { TermsPage } from '../pages/public/TermsPage';
import { ManagedVenuesPage } from '../pages/public/ManagedVenuesPage';
import { VenueBookingWizard } from '../pages/public/VenueBookingWizard';
import { BookingPipeline } from '../pages/public/BookingPipeline';
import { AdminManagedVenues } from '../pages/admin/AdminManagedVenues';
import { AdminEvents } from '../pages/admin/AdminEvents';
import { AdminSpaces } from '../pages/admin/AdminSpaces';
import { AdminOrganizations } from '../pages/admin/AdminOrganizations';
import { AdminCopy } from '../pages/admin/AdminCopy';
import { AdminMedia } from '../pages/admin/AdminMedia';
import { AdminCmsTemplates, AdminCmsTemplateNew } from '../pages/admin/AdminCmsTemplates';
import { AdminCmsPages, AdminCmsPageNew } from '../pages/admin/AdminCmsPages';
import { CmsEditor } from '../pages/admin/CmsEditor';
import {
  AdminCmsGlobalSections, AdminCmsGlobalSectionNew,
} from '../pages/admin/AdminCmsGlobalSections';
import { AdminCmsThemes, AdminCmsThemeNew } from '../pages/admin/AdminCmsThemes';
import { AdmissionStatusPage } from '../pages/public/AdmissionStatusPage';
import { ThemeEditor } from '../pages/admin/ThemeEditor';
import { AdminShell } from '../admin/AdminShell';
import { EventLineupRoute } from '../admin/EventLineupRoute';

/**
 * Route map for the whole portal.
 *
 * Most sections are built. The few that are not render <Placeholder>, which tells a
 * visitor the section is not open yet and nothing more; it used to list the engine
 * endpoints each screen would call, which was a build note reachable on public routes.
 *
 * Guard note — the engine's `requireRole` lets `admin` through every role gate,
 * so each portal allows its own role plus `admin`. Check-in is the exception:
 * the engine gates it on `staff`, not on organizer or venue.
 */
export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    children: [
      // ------------------------------------------------- contract-gated portals
      //
      // OUTSIDE PublicLayout on purpose. These pages own their whole viewport: the
      // background video fills it behind the glass shell, so a site header and footer
      // would sit on top of footage they were never designed against.
      //
      // Anonymous, with no ProtectedRoute. An affiliate has no Voleto account; the
      // contract number is the entire credential, and everything that makes that safe
      // (real entropy in the number, a rate limiter, an identical answer for a wrong
      // number and an unknown one) lives in the engine.
      //
      // Three separate URLs rather than one page with a type selector, per the routing
      // decision: a selector asks someone to identify their own role before they can
      // type a code, and the three dashboards diverge structurally after the gate.
      { path: 'affiliates', element: <AffiliateDashboardPage /> },

      // ---------------------------------------------------------------- public
      {
        element: <PublicLayout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'events', element: <EventsPage /> },
          // The public Event URL is unchanged. EventPageHost resolves the CMS document
          // for this event and renders it through the block registry, falling back to
          // the hardcoded page whenever the CMS path is unavailable.
          { path: 'events/:eventId', element: <EventPageHost /> },
          { path: 'events/:eventId/checkout', element: <CheckoutPage /> },
          // Order confirmation is public: a guest reaches it with the opaque
          // order token from checkout, a signed-in buyer by ownership.
          { path: 'orders/:orderId', element: <OrderConfirmationPage /> },
          // The canonical customer ticket surface. Anonymous by design: the order access
          // token authorises, exactly as it does for the confirmation page above.
          { path: 'orders/:orderId/tickets', element: <TicketsPage /> },
          // §86. ONE status route for every admission kind — a request and a waitlist
          // place answer the same question and the page answers it the same way. The
          // event URL is unchanged; admission behaviour lives inside the event page.
          //
          // Anonymous by design and authorised by a token, exactly as the order pages
          // above are: the id in this URL is not a credential.
          { path: 'admissions/:admissionId/status', element: <AdmissionStatusPage /> },
          { path: 'artists', element: <ArtistsPage /> },
          { path: 'artists/:artistId', element: <ArtistProfilePage /> },
          // The public 506(c) marketplace. Anonymous — no ProtectedRoute — because
          // a 506(c) offering may be generally solicited, and the engine's
          // /api/opportunities/public endpoints need no session.
          //
          // A 506(b) offering can never be reached through any of these: the
          // engine excludes it structurally and returns the same 404 it returns
          // for an unknown id, so there is nothing for this layer to hide.
          { path: 'invest', element: <InvestPage /> },
          // Declared before ':offeringId' so the literal segment wins the match.
          { path: 'invest/interest', element: <RegisterInterestPage /> },
          { path: 'invest/:offeringId', element: <OfferingDetailPage /> },
          // The old placeholders lived at /campaigns and pointed at GET /api/campaigns,
          // which is the EMAIL campaign API — a different feature entirely. Redirected
          // rather than deleted so any existing link still lands somewhere real.
          { path: 'campaigns', element: <Navigate to="/invest" replace /> },
          { path: 'campaigns/:campaignId', element: <Navigate to="/invest" replace /> },
          // A per-ticket page cannot be built yet: the engine exposes only
          // GET /api/tickets/:id/qr, which is buyer-authenticated and owner-only, and no
          // endpoint returning a ticket's own event, tier or status. This route was also
          // filed as PUBLIC while everything it would need requires a signed-in buyer,
          // and it is linked from nowhere.
          //
          // Tickets already work in two places: a guest reaches theirs from the order
          // confirmation via the access token, and a signed-in buyer sees every ticket
          // with its QR at /account/orders. So this redirects there instead of rendering
          // a dead end. ProtectedRoute sends a guest to sign in, which is the correct
          // answer for a URL that can only ever show someone their own ticket.
          { path: 'tickets/:ticketId', element: <Navigate to="/account/orders" replace /> },

          { path: 'login', element: <LoginPage /> },
          { path: 'register', element: <RegisterPage /> },
          { path: 'terms', element: <TermsPage /> },
          // Public managed venue showcase and intake. Open to organizers with no account.
          { path: 'venues', element: <ManagedVenuesPage /> },
          { path: 'venues/bookings/:id/pipeline', element: <BookingPipeline /> },
          { path: 'venues/:slug/book', element: <VenueBookingWizard /> },
          { path: 'forbidden', element: <ForbiddenPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },

      // ----------------------------------------------------------------- buyer
      // Account pages need only a signed-in user; every role has an identity, and
      // the engine authorizes the underlying reads by ownership regardless.
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <PublicLayout />,
            children: [
              { path: 'account', element: <AccountPage /> },
              { path: 'account/orders', element: <OrdersPage /> },
            ],
          },
        ],
      },

      // ------------------------------------------------------------- organizer
      {
        element: <ProtectedRoute allow={['organizer', 'admin']} />,
        children: [
          {
            path: 'organizer',
            element: (
              <PortalLayout
                section="Organizer"
                nav={[
                  { to: '/organizer', label: 'Dashboard', end: true },
                  { to: '/organizer/events', label: 'Events' },
                  { to: '/organizer/orders', label: 'Orders' },
                  { to: '/organizer/bookings', label: 'Bookings' },
                  { to: '/organizer/settlement', label: 'Settlement' },
                  { to: '/organizer/venues', label: 'Venues' },
                  { to: '/organizer/sponsors', label: 'Sponsors' },
                ]}
              />
            ),
            children: [
              { index: true, element: <OrganizerDashboard /> },
              { path: 'events', element: <OrganizerDashboard /> },
              // The wizard commits each step to the engine, so the draft id lands in
              // the URL after step 1 and a reload resumes rather than starting over.
              { path: 'events/new', element: <EventWizard /> },
              { path: 'events/new/:eventId', element: <EventWizard /> },
              { path: 'events/:eventId', element: <EventManage /> },
              {
                path: 'orders',
                element: (
                  <Placeholder
                    title="Orders"
                    description="Order history, refunds, and attendee lists."
                  />
                ),
              },
              {
                path: 'bookings',
                element: (
                  <Placeholder
                    title="Bookings"
                    description="Talent bookings and offers for your events."
                  />
                ),
              },
              // Real screen as of engine migration 066: this is where an organizer sets
              // up the Connect account their net proceeds are transferred to.
              {
                path: 'settlement',
                element: <OrganizerPayouts />,
              },
              {
                path: 'venues',
                element: <VenueBrowse />,
              },
              {
                path: 'sponsors',
                element: (
                  <Placeholder
                    title="Sponsorship"
                    description="Sponsor packages offered against your events."
                  />
                ),
              },
            ],
          },
        ],
      },

      // ---------------------------------------------------------------- investor
      {
        element: <ProtectedRoute allow={['investor', 'admin']} />,
        children: [
          {
            path: 'investor',
            element: (
              <PortalLayout
                section="Investor"
                nav={[
                  { to: '/investor', label: 'Dashboard', end: true },
                  { to: '/investor/opportunities', label: 'Opportunities' },
                  { to: '/investor/investments', label: 'Investments' },
                  { to: '/investor/documents', label: 'Documents' },
                ]}
              />
            ),
            children: [
              { index: true, element: <InvestorDashboard /> },
              { path: 'opportunities', element: <InvestorDashboard /> },
              { path: 'investments', element: <InvestorInvestments /> },
              { path: 'investments/:investmentId', element: <InvestmentLedger /> },
              {
                path: 'documents',
                element: (
                  <Placeholder
                    title="Documents"
                    description="Agreements and compliance paperwork."
                  />
                ),
              },
            ],
          },
        ],
      },

      // ---------------------------------------------------------------- artist
      {
        element: <ProtectedRoute allow={['artist', 'admin']} />,
        children: [
          {
            path: 'artist',
            element: (
              <PortalLayout
                section="Artist"
                nav={[
                  { to: '/artist', label: 'Dashboard', end: true },
                  { to: '/artist/bookings', label: 'Bookings' },
                  { to: '/artist/ratings', label: 'Ratings' },
                  { to: '/artist/profile', label: 'Profile' },
                ]}
              />
            ),
            children: [
              { index: true, element: <ArtistDashboard /> },
              { path: 'bookings', element: <ArtistBookings /> },
              // The detail screen is where a request is actually answered.
              { path: 'bookings/:agreementId', element: <BookingReview /> },
              // Named 'ratings' rather than 'reviews': these are the organizer
              // ratings that feed the reliability score, not audience reviews.
              { path: 'ratings', element: <ArtistRatings /> },
              { path: 'profile', element: <ArtistProfile /> },
            ],
          },
        ],
      },

      // ---------------------------------------------------------------- agency
      {
        element: <ProtectedRoute allow={['agency', 'admin']} />,
        children: [
          {
            path: 'agency',
            element: (
              <PortalLayout
                section="Agency"
                nav={[
                  { to: '/agency', label: 'Dashboard', end: true },
                  { to: '/agency/roster', label: 'Roster' },
                  { to: '/agency/bookings', label: 'Bookings' },
                  { to: '/agency/opportunities', label: 'Opportunities' },
                ]}
              />
            ),
            children: [
              {
                index: true,
                element: (
                  <Placeholder
                    title="Agency dashboard"
                    description="Roster activity and commission pipeline."
                  />
                ),
              },
              {
                path: 'roster',
                element: (
                  <Placeholder
                    title="Roster"
                    description="Represented artists and their availability."
                  />
                ),
              },
              {
                path: 'bookings',
                element: (
                  <Placeholder
                    title="Bookings"
                    description="Offers negotiated on behalf of the roster."
                  />
                ),
              },
              {
                path: 'opportunities',
                element: (
                  <Placeholder
                    title="Opportunities"
                    description="Open calls matched to roster artists."
                  />
                ),
              },
            ],
          },
        ],
      },

      // ----------------------------------------------------------------- venue
      {
        element: <ProtectedRoute allow={['venue', 'admin']} />,
        children: [
          {
            path: 'venue',
            element: (
              <PortalLayout
                section="Venue"
                nav={[
                  { to: '/venue', label: 'Dashboard', end: true },
                  { to: '/venue/bookings', label: 'Bookings' },
                  { to: '/venue/calendar', label: 'Calendar' },
                  { to: '/venue/profile', label: 'Profile' },
                ]}
              />
            ),
            children: [
              { index: true, element: <VenueDashboard /> },
              { path: 'bookings', element: <VenueBookings /> },
              { path: 'calendar', element: <VenueCalendar /> },
              { path: 'profile', element: <VenueProfilePage /> },
              // Aggregate door counts for one confirmed booking. No buyer data.
              { path: 'events/:eventId', element: <VenueEventDay /> },
            ],
          },
        ],
      },

      // --------------------------------------------------------------- sponsor
      {
        element: <ProtectedRoute allow={['sponsor', 'admin']} />,
        children: [
          {
            path: 'sponsor',
            element: (
              <PortalLayout
                section="Sponsor"
                nav={[
                  { to: '/sponsor', label: 'Dashboard', end: true },
                  { to: '/sponsor/opportunities', label: 'Opportunities' },
                  { to: '/sponsor/activations', label: 'Activations' },
                ]}
              />
            ),
            children: [
              {
                index: true,
                element: (
                  <Placeholder
                    title="Sponsor dashboard"
                    description="Active sponsorships and delivered impressions."
                  />
                ),
              },
              {
                path: 'opportunities',
                element: (
                  <Placeholder
                    title="Opportunities"
                    description="Available sponsorship packages."
                  />
                ),
              },
              {
                path: 'activations',
                element: (
                  <Placeholder
                    title="Activations"
                    description="Committed activations and their deliverables."
                  />
                ),
              },
            ],
          },
        ],
      },

      // -------------------------------------------------------------- check-in
      // Engine gates /api/checkin on `staff` (admin passes as always).
      {
        element: <ProtectedRoute allow={['staff', 'admin']} />,
        children: [
          {
            path: 'checkin',
            element: <CheckinLayout />,
            children: [
              {
                index: true,
                element: (
                  <Placeholder
                    title="Check-in"
                    description="Pick a door and start scanning. Built as an installable PWA for offline-tolerant door use."
                  />
                ),
              },
              {
                path: 'scan/:eventId',
                element: (
                  <Placeholder
                    title="Scanner"
                    description="Camera QR scanning with immediate accept/reject feedback."
                  />
                ),
              },
              {
                path: 'stats/:eventId',
                element: (
                  <Placeholder
                    title="Door stats"
                    description="Live scanned-versus-sold counts. No buyer data by design."
                  />
                ),
              },
            ],
          },
        ],
      },

      // ----------------------------------------------------------------- admin
      //
      // ONE PERSISTENT SHELL, and the shared Event screens mount INSIDE it.
      //
      // The console used to be a PortalLayout like any other role portal, and EventWizard
      // and EventManage existed only under /organizer. So an admin who clicked "Create
      // event" was navigated out of this route tree: React Router unmounted the console
      // and mounted the Organizer shell, and the admin sidebar vanished in the middle of
      // the task. The components below are the SAME components the organizer routes use —
      // imported from ../pages/organizer, not copied — and AdminShell supplies the route
      // base so their internal links stay under /admin.
      //
      // `handle.crumb` is route metadata the shell reads through useMatches(). Breadcrumbs
      // are never built by splitting the URL: a path segment is an id, not a label. Where
      // a crumb needs an entity's NAME, the page publishes it through usePageChrome, which
      // costs no extra fetch because the page has already loaded the entity.
      {
        // Admin ONLY, not staff. Every engine endpoint behind this console is
        // requireAdmin, so admitting staff here would show them a console where
        // every request comes back 403.
        element: <ProtectedRoute allow={['admin']} />,
        children: [
          {
            path: 'admin',
            element: <AdminShell />,
            children: [
              { index: true, element: <AdminHome /> },

              // -- Events. Admin-native routes onto the shared components.
              {
                path: 'events',
                handle: { crumb: 'Events' },
                children: [
                  { index: true, element: <AdminEvents /> },
                  {
                    path: 'new',
                    handle: { crumb: 'Create event' },
                    children: [
                      { index: true, element: <EventWizard /> },
                      // The wizard puts the draft id in the URL after step 1. Under the
                      // admin base that push stays inside /admin, which is the specific
                      // navigation that used to eject the admin to /organizer.
                      { path: ':eventId', element: <EventWizard /> },
                    ],
                  },
                  {
                    path: ':eventId',
                    children: [
                      { index: true, element: <EventManage /> },
                      { path: 'lineup', handle: { crumb: 'Lineup' }, element: <EventLineupRoute /> },
                    ],
                  },
                ],
              },

              { path: 'orders', handle: { crumb: 'Orders' }, element: <AdminOrders /> },
              { path: 'compliance', handle: { crumb: 'Compliance' }, element: <AdminCompliance /> },

              // -- Rentals and venues. The rental pipeline, its calendar and its rate card
              //    are three tabs of one existing screen, addressable so the grouped nav can
              //    point at each. Same component, no duplication.
              {
                path: 'rentals',
                handle: { crumb: 'Rentals' },
                children: [
                  { index: true, element: <AdminManagedVenues /> },
                  { path: 'calendar', handle: { crumb: 'Calendar' }, element: <AdminManagedVenues initialTab="Calendar" /> },
                  { path: 'rate-card', handle: { crumb: 'Rate cards' }, element: <AdminManagedVenues initialTab="Rate card" /> },
                  { path: ':bookingId', handle: { crumb: 'Rental' }, element: <BookingPipeline /> },
                ],
              },
              {
                path: 'venues',
                handle: { crumb: 'Venues' },
                children: [
                  { index: true, element: <AdminVenues /> },
                  // Per-venue calendar: the same managed-venue screen, scoped by the id in
                  // the path. listCalendarBlocks already accepts a venueId.
                  {
                    path: ':venueId/calendar',
                    handle: { crumb: 'Calendar' },
                    element: <AdminManagedVenues initialTab="Calendar" />,
                  },
                ],
              },
              { path: 'spaces', handle: { crumb: 'Spaces' }, element: <AdminSpaces /> },

              // -- Content & design.
              //
              // Copy and Media keep the flat paths they were built with; Pages and
              // Templates are nested under /content because that is where the frozen
              // hierarchy puts them and they are new. Themes and Global Sections are
              // still unbuilt and have no route, which is why the nav does not list them.
              { path: 'copy', handle: { crumb: 'Copy' }, element: <AdminCopy /> },
              { path: 'media', handle: { crumb: 'Media' }, element: <AdminMedia /> },
              {
                path: 'content',
                handle: { crumb: 'Content & design' },
                children: [
                  {
                    path: 'templates',
                    handle: { crumb: 'Templates' },
                    children: [
                      { index: true, element: <AdminCmsTemplates /> },
                      // Before ':templateId', so "new" is a screen rather than an id.
                      { path: 'new', handle: { crumb: 'New' }, element: <AdminCmsTemplateNew /> },
                      { path: ':templateId', element: <CmsEditor kind="template" /> },
                    ],
                  },
                  {
                    path: 'pages',
                    handle: { crumb: 'Pages' },
                    children: [
                      { index: true, element: <AdminCmsPages /> },
                      { path: 'new', handle: { crumb: 'New' }, element: <AdminCmsPageNew /> },
                      { path: ':pageId', element: <CmsEditor kind="page" /> },
                    ],
                  },
                  // Global sections use THE SAME editor as templates and pages, at the
                  // global_section context. A separate section editor would be a second
                  // implementation of save, publish, history and rollback, and the
                  // forgotten copy is always the one that breaks.
                  {
                    path: 'global-sections',
                    handle: { crumb: 'Global sections' },
                    children: [
                      { index: true, element: <AdminCmsGlobalSections /> },
                      { path: 'new', handle: { crumb: 'New' }, element: <AdminCmsGlobalSectionNew /> },
                      { path: ':sectionId', element: <CmsEditor kind="global_section" /> },
                    ],
                  },
                  // Themes do NOT: a theme is a flat set of values with no tree, so the
                  // block editor's outline and palette would both be empty. Its form is
                  // generated from the token schema instead.
                  {
                    path: 'themes',
                    handle: { crumb: 'Themes' },
                    children: [
                      { index: true, element: <AdminCmsThemes /> },
                      { path: 'new', handle: { crumb: 'New' }, element: <AdminCmsThemeNew /> },
                      { path: ':themeId', element: <ThemeEditor /> },
                    ],
                  },
                ],
              },

              // -- Promoters and sales
              { path: 'affiliates', handle: { crumb: 'Affiliates' }, element: <AdminAffiliates /> },

              // -- Investors
              {
                path: 'opportunities',
                handle: { crumb: 'Opportunities' },
                children: [
                  { index: true, element: <AdminOpportunities /> },
                  // "Preview as public would see it". A separate admin-only read of the
                  // same projection the public page uses — it publishes nothing.
                  {
                    path: ':offeringId/preview',
                    handle: { crumb: 'Preview' },
                    element: <AdminOfferingPreview />,
                  },
                ],
              },
              { path: 'investor-approvals', handle: { crumb: 'Investor approvals' }, element: <AdminInvestorApprovals /> },
              { path: 'investments', handle: { crumb: 'Investments' }, element: <AdminInvestments /> },
              { path: 'spvs', handle: { crumb: 'SPVs' }, element: <AdminSpvs /> },
              { path: 'distributions', handle: { crumb: 'Distributions' }, element: <AdminDistributions /> },
              { path: 'network', handle: { crumb: '506(b) network' }, element: <AdminNetwork /> },

              // -- Finance
              { path: 'financials', handle: { crumb: 'Revenue' }, element: <AdminFinancials /> },

              // -- Operations
              { path: 'artists', handle: { crumb: 'Artist approvals' }, element: <AdminArtists /> },
              { path: 'audit', handle: { crumb: 'Audit log' }, element: <AdminAudit /> },
              { path: 'personas', handle: { crumb: 'Test personas' }, element: <AdminPersonas /> },

              // -- Platform
              { path: 'organizations', handle: { crumb: 'Organizations' }, element: <AdminOrganizations /> },
              { path: 'users', handle: { crumb: 'Users' }, element: <AdminUsers /> },
              { path: 'plugins', handle: { crumb: 'Modules / plugins' }, element: <AdminPlugins /> },

              // Old flat-nav URLs an admin may have bookmarked. Kept working rather than
              // 404ing, because the nav changing shape is not a reason to break a link.
              { path: 'managed-venues', element: <Navigate to="/admin/rentals" replace /> },
            ],
          },
        ],
      },

      // Convenience alias — the engine calls this role `staff`.
      { path: 'staff', element: <Navigate to="/checkin" replace /> },
    ],
  },
]);
